// ============================================================================
// quote-corpus-nomination.js — nominate a QuoteMaker quote into the corpus.
// ----------------------------------------------------------------------------
// "Good examples get saved, but not always": a nomination NEVER enters
// retrieval directly. It lands as pending_review in quote_corpus_entries and
// waits for operator approval in the DevTools Quote Corpus panel.
//
// Caller: EvidenceDrawer "Corpus" chip (manual, operator-triggered). Marking a
// quote Won is an outcome only and has no corpus-admission authority.
//
// The browser sends only the delivery-bound identifiers plus review metadata.
// The backend reads the verbatim Client Quote text and totals from the exact
// immutable artifact package; no caller-assembled body has admission authority.
// No AI runs here; scoring is a separate operator-triggered action.
// ============================================================================

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// Coarse job-type inference over the corpus' 21 slugs. This only seeds the
// pending_review draft — the operator confirms or corrects at review time.
const SLUG_KEYWORDS = [
    ['roofing', ['roof', 'gutter', 'downpipe', 'flashing', 'tile ridge', 'sarking', 'whirlybird']],
    ['waterproofing_water_ingress', ['waterproof', 'membrane', 'water ingress', 'damp', 'leak', 'seepage']],
    ['balcony_balustrade', ['balcony', 'balustrade', 'handrail', 'veranda']],
    ['painting', ['paint', 'repaint', 'render finish']],
    ['carpentry_timber', ['timber', 'deck', 'pergola', 'fascia', 'eave', 'soffit', 'ramp', 'awning']],
    ['windows_doors', ['window', 'door', 'flyscreen', 'lock', 'sliding']],
    ['masonry_brickwork', ['brick', 'repoint', 'masonry', 'retaining wall', 'stone capping', 'lintel']],
    ['concrete_structural', ['spalling', 'concrete cancer', 'slab', 'structural steel', 'post anchor']],
    ['paving_driveways_external_tiling', ['driveway', 'pathway', 'paving', 'pebblecrete', 'bitumen']],
    ['tiling_wet_area', ['regrout', 'wet area', 'bathroom tile', 'shower tile']],
    ['bathroom_kitchen_reno', ['bathroom renovation', 'kitchen renovation', 'laundry renovation']],
    ['flooring_internal', ['flooring', 'carpet', 'magnesite', 'floor level']],
    ['ceilings_walls_internal', ['ceiling', 'plasterboard', 'cornice', 'gyprock', 'internal crack']],
    ['fencing_gates', ['fence', 'fencing', 'gate', 'pool fence']],
    ['plumbing_drainage', ['stormwater', 'drainage', 'drain', 'sewer', 'plumb']],
    ['electrical_lighting', ['light', 'electrical', 'power point']],
    ['asbestos_removal', ['asbestos', 'acm']],
    ['diagnostic_investigation', ['flood test', 'investigation', 'inspection report', 'leak detection']],
    ['site_amenities_structures', ['bin area', 'letterbox', 'carport', 'clothesline', 'shed', 'signage']],
];

/**
 * Score every slug and report the winner ALONG WITH how contested it was.
 *
 * `ambiguous` is the load-bearing addition. The scan keeps the best slug on a
 * strict `score > bestScore`, so when two slugs tie, the winner is decided by
 * position in SLUG_KEYWORDS — array order, not evidence. That is invisible and
 * it bites: "Concrete spalling repairs to soffit" scores 1 for
 * `carpentry_timber` ('soffit') and 1 for `concrete_structural` ('spalling'),
 * and carpentry wins purely because it is declared three entries earlier.
 * Verified live 2026-08-09 against the 1,424-entry corpus — deleting the single
 * word "soffit" flipped the slug and the retrieved exemplars from three
 * roof/awning quotes to three genuine spalling quotes.
 *
 * There is no principled way to break such a tie from keyword counts, so this
 * does not try. It reports the coin-flip and lets each caller decide:
 *   - nomination keeps the seed label (operator confirms at review anyway)
 *   - retrieval declines to hard-filter on a coin-flip
 *
 * @param {Array<{title?: string, description?: string, descriptionBrief?: string}>} scopeItems
 * @returns {{slug: string, score: number, ambiguous: boolean}}
 *   `ambiguous` is true when two or more slugs share the top non-zero score.
 */
export function inferJobType(scopeItems) {
    const text = (scopeItems || [])
        .map((s) => `${s?.title || ''} ${s?.description || s?.descriptionBrief || ''}`)
        .join(' ')
        .toLowerCase();
    let best = null;
    let bestScore = 0;
    let tiedAtBest = 0;
    for (const [slug, keywords] of SLUG_KEYWORDS) {
        let score = 0;
        for (const kw of keywords) {
            if (text.includes(kw)) score += kw.includes(' ') ? 2 : 1;
        }
        if (score > bestScore) { best = slug; bestScore = score; tiedAtBest = 1; }
        else if (score > 0 && score === bestScore) { tiedAtBest++; }
    }
    if (!best) return { slug: 'other', score: 0, ambiguous: false };
    return { slug: best, score: bestScore, ambiguous: tiedAtBest > 1 };
}

/**
 * The slug alone — unchanged behaviour, including the array-order tie-break.
 * Nomination labelling depends on this and is deliberately left bit-identical:
 * a nomination lands as pending_review and the operator confirms or corrects
 * the job type at review time, so a contested seed is already caught by a human.
 */
export function inferJobTypeSlug(scopeItems) {
    return inferJobType(scopeItems).slug;
}

/**
 * Assemble the nomination body from the produced quote's own content.
 * The output is what the quote SAID — item titles, description lines and
 * amounts as produced — so the corpus entry reads like the document.
 */
export function buildNominationBody(project) {
    const qd = project?.quoteData || {};
    const scope = Array.isArray(qd.scope) ? qd.scope : [];
    const lines = [];

    const title = qd.header?.title || project?.projectTitle || project?.title || '';
    if (title) lines.push(title, '');

    const objective = qd.scopeObjective || qd.objective || '';
    if (objective) lines.push('Objective', '', objective, '');

    if (scope.length) {
        lines.push('Details', '');
        for (const item of scope) {
            const head = [item.code, item.title].filter(Boolean).join('. ');
            if (head) lines.push(head);
            const desc = item.description || item.descriptionBrief || '';
            if (desc) lines.push(desc);
            if (item.amount != null && item.amount !== '') {
                const n = Number(item.amount);
                if (Number.isFinite(n)) lines.push(`$${n.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`);
            }
            lines.push('');
        }
    }
    return lines.join('\n').trim();
}

// ── Whose voice is actually in this quote? ───────────────────────────────────
// A won quote is proof the CLIENT accepted it. It is not proof the OPERATOR
// wrote it — he may have sent the generated draft untouched. That distinction
// is the whole game for a voice corpus: nominating the model's own prose back
// into the corpus it learns voice from is how the voice drifts to the model's
// mean, one generation at a time.
//
// Scope items already record who last touched them. Generator-authored markers
// are known and finite; anything else (including a missing marker on a legacy
// item) is treated as generator-authored, so an unprovable claim of operator
// authorship is never made. The measurement is honest about being coarse: it
// counts ITEMS TOUCHED, not words written.
const GENERATOR_AUTHORS = new Set([
    'ai-initial',           // first generation
    'wms-writeback',        // Stage 3 work-method write-back
    'ai-requote-shortcut',  // requote path
    'assistant',            // QM Assistant wrote the text
    'assistant-edit',       // ... and its edit variant
    'client-source',        // lifted from the client's RFQ — not the operator's voice either
]);

/**
 * How much of this quote the operator actually authored.
 * @returns {{edited: number, total: number, ratio: number, operatorAuthored: boolean}}
 */
export function measureOperatorAuthorship(project) {
    const scope = Array.isArray(project?.quoteData?.scope) ? project.quoteData.scope : [];
    let edited = 0;
    for (const item of scope) {
        const by = typeof item?.editedBy === 'string' ? item.editedBy : '';
        if (by && !GENERATOR_AUTHORS.has(by)) edited++;
    }
    const total = scope.length;
    const ratio = total > 0 ? edited / total : 0;
    // A majority of items in his hand before the quote counts as his voice.
    // Below that the entry still enters the queue — it is simply labelled for
    // what it is, and the operator decides.
    return { edited, total, ratio, operatorAuthored: total > 0 && ratio >= 0.5 };
}

/**
 * Nominate a project's produced quote into the corpus review queue.
 * Returns the route response ({id, status} | {alreadyNominated: true}).
 * Throws on validation/transport errors — callers decide fail-soft vs surface.
 */
export async function nominateProject(project, { apiFetch, deliveryProvenance } = {}) {
    if (!apiFetch) {
        ({ apiFetch } = await import('./data-service-api.js'));
    }
    const provenance = deliveryProvenance || project?.quoteArtifactDeliveryProvenance || null;
    const artifactId = provenance?.artifactId;
    const artifactPackageHash = provenance?.artifactPackageHash || provenance?.packageHash;
    const deliveryEventId = provenance?.deliveryEventId;
    if (!UUID_RE.test(String(artifactId || ''))
        || !/^sha256:[0-9a-f]{64}$/.test(String(artifactPackageHash || ''))
        || !UUID_RE.test(String(deliveryEventId || ''))) {
        throw new Error('Exact sent-package delivery evidence is required before corpus nomination. Finalised, downloaded or Won is not sent proof.');
    }
    const scope = Array.isArray(project?.quoteData?.scope) ? project.quoteData.scope : [];
    const payload = {
        artifactId,
        artifactPackageHash,
        deliveryEventId,
        jobType: inferJobTypeSlug(scope),
        summary: (project?.quoteData?.scopeObjective || project?.projectTitle || project?.title || '').slice(0, 200) || undefined,
        workTypeTags: [...new Set(scope
            .map(item => item?.workTypeSlug || item?.work_type_slug || item?.category)
            .filter(value => typeof value === 'string' && value.trim()))],
        // Rides into the review queue so the operator can see, without opening
        // the quote, whether he wrote this one or the generator did.
        flags: (() => {
            const a = measureOperatorAuthorship(project);
            return [a.operatorAuthored ? 'voice:operator' : 'voice:generator',
                `edited:${a.edited}/${a.total}`];
        })(),
    };
    return apiFetch('/quote-corpus/nominations', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}
